/** Building blocks: big buttons, labelled inputs, toggles, chips, status pills, banners. */
import React, { useId } from "react";
import type { UiStatus } from "@core/status";
import type { Strings } from "../i18n";

type Variant = "primary" | "secondary" | "danger" | "ghost" | "sos";

export function Button({
  children,
  onClick,
  variant = "primary",
  disabled,
  busy,
  big,
  sub,
  type = "button",
  className = "",
  testId,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: Variant;
  disabled?: boolean;
  busy?: boolean;
  big?: boolean;
  sub?: React.ReactNode;
  type?: "button" | "submit";
  className?: string;
  testId?: string;
  ariaLabel?: string;
}) {
  return (
    <button
      type={type}
      className={`btn btn-${variant}${big ? " btn-big" : ""} ${className}`}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      {busy ? <span className="spinner" aria-hidden /> : null}
      <span className="btn-text">
        {children}
        {sub ? <span className="btn-sub">{sub}</span> : null}
      </span>
    </button>
  );
}

export function LinkButton({ href, children, variant = "secondary", big, sub, testId }: { href: string; children: React.ReactNode; variant?: Variant; big?: boolean; sub?: React.ReactNode; testId?: string }) {
  return (
    <a className={`btn btn-${variant}${big ? " btn-big" : ""}`} href={href} data-testid={testId}>
      <span className="btn-text">
        {children}
        {sub ? <span className="btn-sub">{sub}</span> : null}
      </span>
    </a>
  );
}

export function Field({
  label,
  value,
  onChange,
  error,
  hint,
  inputMode,
  type = "text",
  maxLength,
  placeholder,
  readOnly,
  multiline,
  autoComplete,
  name,
  className = "",
  onEnter,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  hint?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  type?: string;
  maxLength?: number;
  placeholder?: string;
  readOnly?: boolean;
  multiline?: boolean;
  autoComplete?: string;
  name?: string;
  className?: string;
  onEnter?: () => void;
}) {
  const id = useId();
  const common = {
    id,
    name,
    value,
    readOnly,
    maxLength,
    placeholder,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error || hint ? `${id}-d` : undefined,
    className: `input${error ? " input-error" : ""}`,
  };
  return (
    <div className={`field ${className}`}>
      <label htmlFor={id} className="label">
        {label}
      </label>
      {multiline ? (
        <textarea {...common} rows={2} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input
          {...common}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onEnter ? (e) => e.key === "Enter" && onEnter() : undefined}
        />
      )}
      {error ? (
        <div id={`${id}-d`} className="error" role="alert">
          ⚠ {error}
        </div>
      ) : hint ? (
        <div id={`${id}-d`} className="hint">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

export const NumberField = (p: Omit<React.ComponentProps<typeof Field>, "inputMode">) => <Field {...p} inputMode="numeric" maxLength={p.maxLength ?? 6} autoComplete="off" />;
// 20: room for "+91 98765-43210" and similar; the rules strip spaces, dashes and the +91 / 0 prefix
export const PhoneField = (p: Omit<React.ComponentProps<typeof Field>, "inputMode" | "type">) => <Field {...p} inputMode="tel" type="tel" maxLength={20} autoComplete="tel" />;

export function Toggle({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className={`toggle${disabled ? " disabled" : ""}`}>
      <span>{label}</span>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-ui" aria-hidden />
    </label>
  );
}

export function Choice<T extends string>({ options, value, onChange, disabled, name }: { options: { value: T; label: string }[]; value: T | ""; onChange: (v: T | "") => void; disabled?: boolean; name: string }) {
  return (
    <div className="chips" role="radiogroup" aria-label={name}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} className={`chip${on ? " chip-on" : ""}`} disabled={disabled} onClick={() => onChange(on ? "" : o.value)}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export const statusLabel = (t: Strings, s: UiStatus) => t[`st_${s}` as const];

export function StatusPill({ status, t }: { status: UiStatus; t: Strings }) {
  return (
    <span className={`pill st-${status}`}>
      <span className="dot" aria-hidden />
      {statusLabel(t, status)}
    </span>
  );
}

export function StatusDot({ status, t }: { status: UiStatus; t: Strings }) {
  return <span className={`sdot st-${status}`} title={statusLabel(t, status)} aria-label={statusLabel(t, status)} role="img" />;
}

export function Banner({ kind, children, action, testId }: { kind: "info" | "warn" | "error" | "ok"; children: React.ReactNode; action?: { label: string; onClick: () => void }; testId?: string }) {
  return (
    <div className={`banner banner-${kind}`} role={kind === "error" || kind === "warn" ? "alert" : "status"} data-testid={testId}>
      <div>{children}</div>
      {action ? (
        <Button variant="secondary" onClick={action.onClick} className="banner-action">
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

export function Card({ children, className = "", testId }: { children: React.ReactNode; className?: string; testId?: string }) {
  return (
    <section className={`card ${className}`} data-testid={testId}>
      {children}
    </section>
  );
}
